import { useEffect, useState } from 'react';
import { editorStore } from './stores/editorStore';
import { api } from '../lib/api/client';
import { CanvasPreview } from './components/canvas/CanvasPreview';
import { LibraryPanel } from './components/library/LibraryPanel';
import { PropertiesPanel } from './components/properties/PropertiesPanel';
import { TimelinePanel } from './components/timeline/TimelinePanel';
import './EditorPage.css';

/**
 * 可视化编辑器主页面（Phase 3 · Ticket 03 增强版）
 * - sessionId：AI 生成完成后自动传入（?mode=editor&session={id}），拉取数据填充 editorStore
 * - 人工确认：每页提供「确认/修改」按钮，未确认页显示黄色标记
 * - 一键发布：全部确认后发布到图书馆
 */
export function EditorPage({ sessionId, onExit }: { sessionId?: string; onExit?: () => void }) {
  const book = editorStore((s) => s.book);
  const createBook = editorStore((s) => s.createBook);
  const loadAIGeneratedBook = editorStore((s) => s.loadAIGeneratedBook);
  const undo = editorStore((s) => s.undo);
  const redo = editorStore((s) => s.redo);
  const addPage = editorStore((s) => s.addPage);
  const deletePage = editorStore((s) => s.deletePage);
  const currentPageId = editorStore((s) => s.currentPageId);
  const setCurrentPage = editorStore((s) => s.setCurrentPage);
  const publishBook = editorStore((s) => s.publishBook);

  const [loading, setLoading] = useState(!!sessionId);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [confirmedPages, setConfirmedPages] = useState<Set<string>>(new Set());
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState<{success: boolean; slug?: string; error?: string} | null>(null);

  // 若有 sessionId，从 AI 生成结果加载绘本数据
  useEffect(() => {
    if (!sessionId) {
      if (!book) createBook();
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await api.ai.status(sessionId);
        if (!cancelled) {
          if (data.status === 'completed' && data.pages) {
            loadAIGeneratedBook(
              data.pages.map((p: any) => ({
                page_number: p.page_number,
                title: p.title,
                content: p.content,
                image_path: p.image_path,
              })),
              data.theme || 'AI 生成的绘本'
            );
          } else {
            setLoadError('AI 生成尚未完成或已失败，请返回生成器重试');
          }
        }
      } catch (err: any) {
        if (!cancelled) setLoadError(err.message || '加载 AI 生成数据失败');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // 首次无 sessionId 时自动创建
  useEffect(() => {
    if (!sessionId && !book) createBook();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, book]);

  // 快捷键
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo]);

  if (loading) {
    return (
      <div style={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1a1a2e', color: '#fff', fontFamily: 'system-ui, sans-serif' }}>
        <div>正在加载 AI 生成结果...</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#1a1a2e', color: '#fff', fontFamily: 'system-ui, sans-serif', gap: '16px' }}>
        <div style={{ fontSize: '28px' }}>⚠️</div>
        <div>{loadError}</div>
        <button onClick={() => window.location.href = '?mode=ai-generator'} style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: '#7c4dff', color: '#fff', cursor: 'pointer' }}>
          返回 AI 生成器
        </button>
      </div>
    );
  }

  if (!book) {
    return <div className="editor-loading">正在初始化编辑器…</div>;
  }

  const allConfirmed = book.pages.length > 0 && book.pages.every((p) => confirmedPages.has(p.id));

  const exportBook = () => {
    const data = JSON.stringify(book, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${book.title || 'book'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePublish = async () => {
    if (!allConfirmed) return;
    setPublishing(true);
    const result = await publishBook();
    setPublishing(false);
    setPublishResult(result);
    if (result.success && result.slug) {
      // 成功：3 秒后跳转到图书馆
      setTimeout(() => {
        window.location.href = `?mode=library`;
      }, 3000);
    }
  };

  return (
    <div className="editor-root">
      {/* 顶栏 */}
      <header className="editor-toolbar" style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 16px', background: '#1a1a2e', borderBottom: '1px solid #333' }}>
        <div className="editor-brand" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span className="editor-brand-logo">📖</span>
          <span className="editor-brand-name">绘本工厂</span>
        </div>

        <input
          className="editor-title-input"
          value={book.title}
          onChange={(e) =>
            editorStore.setState({
              book: { ...book, title: e.target.value, updated: new Date().toISOString() },
            })
          }
          placeholder="绘本标题"
          style={{ flex: 1, maxWidth: '300px' }}
        />

        {/* 页面切换器 + 确认标记 */}
        <div className="editor-page-switcher" style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: 1, overflow: 'auto' }}>
          {book.pages.map((p, i) => {
            const confirmed = confirmedPages.has(p.id);
            return (
              <button
                key={p.id}
                className={`editor-page-tab${p.id === currentPageId ? ' editor-page-tab-active' : ''}`}
                onClick={() => setCurrentPage(p.id)}
                title={p.title}
                style={{
                  border: p.id === currentPageId ? '2px solid #448aff' : confirmed ? '2px solid #4ade80' : '2px solid transparent',
                  position: 'relative',
                }}
              >
                {i + 1}
                {!confirmed && (
                  <span style={{ position: 'absolute', top: '-4px', right: '-4px', width: '8px', height: '8px', borderRadius: '50%', background: '#fbbf24' }} />
                )}
              </button>
            );
          })}
          <button className="editor-icon-btn" title="新增页面" onClick={addPage}>
            +
          </button>
          {book.pages.length > 1 && (
            <button
              className="editor-icon-btn"
              title="删除当前页"
              onClick={() => currentPageId && deletePage(currentPageId)}
            >
              🗑
            </button>
          )}
        </div>

        {/* 发布按钮 */}
        <div style={{ display: 'flex', gap: '8px' }}>
          {sessionId && (
            <>
              <button
                onClick={() => {
                  if (!currentPageId) return;
                  setConfirmedPages((prev) => {
                    const next = new Set(prev);
                    next.add(currentPageId);
                    return next;
                  });
                }}
                style={{
                  padding: '6px 14px',
                  borderRadius: '8px',
                  border: 'none',
                  background: confirmedPages.has(currentPageId || '') ? '#4ade80' : '#fbbf24',
                  color: '#0b1220',
                  fontSize: '13px',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                {confirmedPages.has(currentPageId || '') ? '✓ 已确认' : '确认本页'}
              </button>
              <button
                onClick={handlePublish}
                disabled={!allConfirmed || publishing}
                style={{
                  padding: '6px 16px',
                  borderRadius: '8px',
                  border: 'none',
                  background: allConfirmed ? 'linear-gradient(135deg, #7c4dff, #448aff)' : '#555',
                  color: '#fff',
                  fontSize: '13px',
                  cursor: allConfirmed ? 'pointer' : 'not-allowed',
                  fontWeight: 600,
                }}
              >
                {publishing ? '发布中...' : '🚀 发布到图书馆'}
              </button>
            </>
          )}
          <button onClick={exportBook} style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid #555', background: 'transparent', color: '#aaa', fontSize: '13px', cursor: 'pointer' }}>
            导出
          </button>
          {onExit && (
            <button onClick={onExit} style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid #555', background: 'transparent', color: '#aaa', fontSize: '13px', cursor: 'pointer' }}>
              退出
            </button>
          )}
        </div>
      </header>

      {/* 发布结果提示 */}
      {publishResult && (
        <div style={{
          padding: '10px 16px',
          background: publishResult.success ? 'rgba(74,222,128,0.15)' : 'rgba(255,100,100,0.15)',
          borderBottom: `1px solid ${publishResult.success ? '#4ade80' : '#ff8a8a'}`,
          color: publishResult.success ? '#4ade80' : '#ff8a8a',
          fontSize: '13px',
          textAlign: 'center',
        }}>
          {publishResult.success
            ? `🎉 绘本「${book.title}」已发布！3 秒后跳转到图书馆...`
            : `❌ 发布失败：${publishResult.error}`}
        </div>
      )}

      {/* 主体 */}
      <div className="editor-body" style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <LibraryPanel />
        <div className="editor-canvas-wrap" style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          <CanvasPreview />
        </div>
        <PropertiesPanel />
      </div>
      <TimelinePanel />
    </div>
  );
}

export default EditorPage;